import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import {
  CIDocForm,
  ClaveUnicaRetryScreen,
  QuestionsChallengeForm,
  VerifyClaveUnicaLogin,
} from "./components";
import { Modal, SnackBar } from "@components";
import {
  FormBody,
  FormContentContainer,
  FormHeader,
  FullScreenLoader,
} from "@components";
import {
  QuestionsChallengeActionError,
  ValidationChallengePayload,
  ValidationChallengeResponse,
  GetQuestionsFormData,
  CIDocFormData,
} from "@interfaces";
import { apiGateway, useDoctorContext } from "@contexts";
import { GetQuestions } from "./components/GetQuestions";
import {
  ErrorType,
  getEnrolmentErrorDisplay,
  onQuestionChallengeAxiosErrorInterpreter,
} from "./hooks/onQuestionChallengeError";
import { ENROLMENT_STEP, ENROLMENT_STEP_STATUS } from "@mekidoc/types";
import {
  EnrolmentStatus,
  InlineError,
  useEnrolmentStatusContext,
} from "@contexts/EnrolmentStatusContext";
import { ClaveUnicaLogin } from "./components/ClaveUnicaLogin";
import { useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import { useBankTransferAttempts } from "@hooks";
import { useEnrolmentErrorHandler } from "./hooks/useEnrolmentErrorHandler";
import { tracker } from "@utils/openreplay";

const RENDERABLE_STEP_STATUSES: ENROLMENT_STEP_STATUS[] = [
  ENROLMENT_STEP_STATUS.NOT_STARTED,
  ENROLMENT_STEP_STATUS.STARTED,
];

const STEPS_BEFORE_BANK_TRANSFER: ENROLMENT_STEP[] = [
  ENROLMENT_STEP.CI_DOC_NUM,
  ENROLMENT_STEP.CLAVE_UNICA,
  ENROLMENT_STEP.QUESTIONS_CHALLENGE,
];

type ClaveUnicaRetryState = {
  description?: string | React.ReactNode;
  nextStep?: ENROLMENT_STEP;
  nextStepStatus?: ENROLMENT_STEP_STATUS;
  canCorrectCiDocNum: boolean;
};

function claveUnicaRetryStateFrom(
  error: Error,
  canCorrectCiDocNum: boolean,
): ClaveUnicaRetryState {
  const { name, description } = getEnrolmentErrorDisplay(error);
  if (error instanceof QuestionsChallengeActionError) {
    return {
      description: description ?? name,
      nextStep: error.nextStep,
      nextStepStatus: error.nextStepStatus,
      canCorrectCiDocNum,
    };
  }
  const errorData = (error as AxiosError<ErrorType>).response?.data;
  return {
    description: description ?? name,
    nextStep: errorData?.nextStep,
    nextStepStatus: errorData?.nextStepStatus,
    canCorrectCiDocNum,
  };
}

const readProvider = async () => {
  try {
    const status = await apiGateway<EnrolmentStatus>({
      url: "/enrolment/orchestrator",
    });
    return status?.provider ?? null;
  } catch {
    return null;
  }
};

export const ValidationChallengeScreen: React.FC = () => {
  const doctor = useDoctorContext();
  const location = useLocation();
  const { requestId } = location.state || {};
  const {
    currentStep,
    currentStepStatus,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
    pendingInlineError,
    setPendingInlineError,
    isMigrationValidated,
    provider,
  } = useEnrolmentStatusContext();

  const { resetAttempts: resetBankTransferAttempts } =
    useBankTransferAttempts();

  const [showUserIsVerifiedSnackbar, setShowUserIsVerifiedSnackbar] =
    useState(false);

  const [claveUnicaRetry, setClaveUnicaRetry] =
    useState<ClaveUnicaRetryState | null>(null);

  const [showValidateDoctorDataModal, setShowValidateDoctorDataModal] =
    useState(false);
  const [showValidateDoctorDataInputs, setShowValidateDoctorDataInputs] =
    useState(false);
  const [docNumErrorDescription, setDocNumErrorDescription] = useState<
    string | React.ReactNode | null
  >(null);

  const ciform = useForm<CIDocFormData>({
    defaultValues: {
      ciDocNum: "",
    },
  });

  const form = useForm<GetQuestionsFormData>({
    defaultValues: {
      name: doctor.name,
      firstSurname: doctor.lastName,
      secondSurname: "",
      birthdate: doctor.birthdate,
    },
  });

  const retryValidationChallengeRef = useRef<(() => void) | null>(null);
  const isRequestingQuestionSetRef = useRef(false);

  const handleInvalidInput = useCallback(
    (error: InlineError) => {
      if (error.nextStep !== ENROLMENT_STEP.CI_DOC_NUM) return false;
      ciform.setError("ciDocNum", { type: "manual", message: error.name });
      setDocNumErrorDescription(error.description ?? null);
      return true;
    },
    [ciform],
  );

  const handleIncompleteData = useCallback(() => {
    setShowValidateDoctorDataModal(true);
    setShowValidateDoctorDataInputs(true);
    return true;
  }, []);

  const { handleEnrolmentError, showErrorAsModal, enrolmentErrorModals } =
    useEnrolmentErrorHandler({
      onRetry: () => retryValidationChallengeRef.current?.(),
      onInvalidInput: handleInvalidInput,
      onIncompleteData: handleIncompleteData,
      fallbackTitle: "Error al generar preguntas",
    });

  useEffect(() => {
    if (!pendingInlineError) return;
    const belongsToCurrentStep = pendingInlineError.nextStep === currentStep;
    if (!belongsToCurrentStep || !handleInvalidInput(pendingInlineError)) {
      showErrorAsModal(pendingInlineError);
    }
    setPendingInlineError(null);
  }, [
    pendingInlineError,
    currentStep,
    handleInvalidInput,
    showErrorAsModal,
    setPendingInlineError,
  ]);

  const {
    mutate: createValidationChallenge,
    isPending: isCreatingValidationChallenge,
    data: questions,
    status: validationChallengeStatus,
  } = useMutation({
    mutationFn: async (
      validationChallengePayload: ValidationChallengePayload,
    ) => {
      const { questions } = await apiGateway<
        ValidationChallengeResponse,
        ErrorType
      >(
        {
          method: "post",
          url: "/enrolment/questions-challenge",
          data: validationChallengePayload,
        },
        onQuestionChallengeAxiosErrorInterpreter,
      );

      return questions;
    },
    onSuccess: async () => {
      setDocNumErrorDescription(null);
      await getEnrolmentStatus();
    },
    onError: handleEnrolmentError,
    onSettled: () => {
      isRequestingQuestionSetRef.current = false;
    },
  });

  const requestQuestionSet = useCallback(
    (validationChallengePayload: ValidationChallengePayload) => {
      if (isRequestingQuestionSetRef.current) return;
      isRequestingQuestionSetRef.current = true;
      createValidationChallenge(validationChallengePayload);
    },
    [createValidationChallenge],
  );

  useEffect(() => {
    retryValidationChallengeRef.current = () => {
      const anotherQuestionSetIsAlreadyOnItsWay =
        validationChallengeStatus === "pending" ||
        validationChallengeStatus === "success";
      if (anotherQuestionSetIsAlreadyOnItsWay) return;
      requestQuestionSet({});
    };
  }, [validationChallengeStatus, requestQuestionSet]);

  const showClaveUnicaError = useCallback(
    async (error: Error, canCorrectCiDocNum: boolean) => {
      console.error("Error on clave unica step: ", error);
      const routesToAnotherStep =
        error instanceof QuestionsChallengeActionError &&
        error.action === "invalidInput" &&
        !!error.nextStep &&
        error.nextStep !== ENROLMENT_STEP.CLAVE_UNICA;
      if (routesToAnotherStep) {
        await handleEnrolmentError(error);
        return;
      }
      setClaveUnicaRetry(claveUnicaRetryStateFrom(error, canCorrectCiDocNum));
    },
    [handleEnrolmentError],
  );

  // BPO gets the saved ID serial with the request for the ClaveÚnica link and
  // checks it there, so that request is the one a mistyped serial can break.
  // Not after the ClaveÚnica login, which no longer uses the serial; and never
  // with eSign, which asks for the serial after ClaveÚnica: saving one first
  // would skip ClaveÚnica.
  // After several failed requests, the back may hand a BPO enrolment over to
  // eSign within this very request, so a BPO job's provider is read again.
  // Only read: unlike getEnrolmentStatus, a failure here must not take the
  // doctor away from the error screen; it just leaves the action out.
  const handleClaveUnicaLinkError = useCallback(
    async (error: Error) => {
      const isStillBpo =
        provider === "bpo" && (await readProvider()) === "bpo";
      await showClaveUnicaError(error, isStillBpo);
    },
    [showClaveUnicaError, provider],
  );

  const handleClaveUnicaLoginError = useCallback(
    (error: Error) => showClaveUnicaError(error, false),
    [showClaveUnicaError],
  );

  // Only on screen: the cert job keeps the saved serial until the form sends a
  // new one, and the orchestrator then resumes at ClaveÚnica with it. The form
  // starts empty because the web app never gets the saved serial back.
  const handleCorrectCiDocNum = useCallback(() => {
    setClaveUnicaRetry(null);
    ciform.reset({ ciDocNum: "" });
    setDocNumErrorDescription(null);
    setCurrentStep(ENROLMENT_STEP.CI_DOC_NUM);
    setCurrentStepStatus(ENROLMENT_STEP_STATUS.NOT_STARTED);
  }, [ciform, setCurrentStep, setCurrentStepStatus]);

  const handleClaveUnicaRetry = useCallback(async () => {
    const nextStep = claveUnicaRetry?.nextStep;
    const nextStepStatus = claveUnicaRetry?.nextStepStatus;
    setClaveUnicaRetry(null);
    if (nextStep && nextStepStatus) {
      setCurrentStep(nextStep);
      setCurrentStepStatus(nextStepStatus);
      return;
    }
    await getEnrolmentStatus();
  }, [
    claveUnicaRetry,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
  ]);

  useEffect(() => {
    if (!currentStep || !STEPS_BEFORE_BANK_TRANSFER.includes(currentStep))
      return;
    resetBankTransferAttempts();
  }, [currentStep, resetBankTransferAttempts]);

  useEffect(() => {
    const getEnrolmentStatusCallback = async () => {
      if (!currentStep) {
        await getEnrolmentStatus();
      }
    };
    getEnrolmentStatusCallback();
  }, [currentStep, getEnrolmentStatus]);

  const isRenderableStepStatus =
    currentStepStatus !== null &&
    RENDERABLE_STEP_STATUSES.includes(currentStepStatus);

  const stepStatusToRender = isRenderableStepStatus
    ? currentStepStatus
    : ENROLMENT_STEP_STATUS.NOT_STARTED;

  useEffect(() => {
    if (!currentStep || isRenderableStepStatus) return;
    console.error("Unhandled enrolment step status, rendering as NOT_STARTED", {
      step: currentStep,
      status: currentStepStatus,
    });
    tracker?.event("UnhandledEnrolmentStepStatus", {
      step: currentStep,
      status: currentStepStatus ?? "null",
    });
  }, [currentStep, currentStepStatus, isRenderableStepStatus]);

  const hasRecoveredOrphanedChallenge = useRef(false);

  useEffect(() => {
    if (
      currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED &&
      !questions &&
      !isCreatingValidationChallenge &&
      !hasRecoveredOrphanedChallenge.current
    ) {
      hasRecoveredOrphanedChallenge.current = true;
      requestQuestionSet({});
    }
  }, [
    currentStep,
    stepStatusToRender,
    questions,
    isCreatingValidationChallenge,
    requestQuestionSet,
  ]);

  const renderScreen = () => {
    // The context is taking a validated migration to its last screen; no step
    // may render meanwhile (the questions step would ask BPO for questions).
    if (isMigrationValidated) {
      return <FullScreenLoader title="Cargando" />;
    }
    if (claveUnicaRetry) {
      return (
        <ClaveUnicaRetryScreen
          description={claveUnicaRetry.description}
          onRetry={handleClaveUnicaRetry}
          onCorrectCiDocNum={
            claveUnicaRetry.canCorrectCiDocNum
              ? handleCorrectCiDocNum
              : undefined
          }
        />
      );
    }
    if (
      currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.NOT_STARTED
    )
      return (
        <ClaveUnicaLogin
          requestId={requestId}
          onError={handleClaveUnicaLinkError}
        />
      );
    if (
      currentStep === ENROLMENT_STEP.CLAVE_UNICA &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED
    )
      return (
        <VerifyClaveUnicaLogin
          setShowUserIsVerifiedSnackbar={setShowUserIsVerifiedSnackbar}
          onError={handleClaveUnicaLoginError}
        />
      );
    if (currentStep === ENROLMENT_STEP.CI_DOC_NUM)
      return (
        <>
          <CIDocForm
            docNumErrorDescription={docNumErrorDescription}
            form={ciform}
            onError={handleEnrolmentError}
          />
          <SnackBar
            snackBarState={{
              open: showUserIsVerifiedSnackbar,
              type: "success",
            }}
            onClose={() => setShowUserIsVerifiedSnackbar(false)}
            alerts={{
              success: "Identidad validada con clave única",
            }}
          />
        </>
      );
    if (
      currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.NOT_STARTED
    ) {
      return (
        <>
          <GetQuestions
            form={form}
            showValidateDoctorDataInputs={showValidateDoctorDataInputs}
            requestQuestionSet={requestQuestionSet}
            isCreatingValidationChallenge={isCreatingValidationChallenge}
          />
          <Modal
            title="Se requiere información adicional"
            content="Algunos datos del Registro Nacional de Prestadores Individuales de Salud no fueron encontrados. Por favor, completa los nuevos campos requeridos."
            isOpen={showValidateDoctorDataModal}
            onClose={() => setShowValidateDoctorDataModal(false)}
            buttonProps={{
              buttonText: "Aceptar",
              onClick: () => setShowValidateDoctorDataModal(false),
            }}
          />
          <SnackBar
            snackBarState={{
              open: showUserIsVerifiedSnackbar,
              type: "success",
            }}
            onClose={() => setShowUserIsVerifiedSnackbar(false)}
            alerts={{
              success: "Identidad validada con clave única",
            }}
          />
        </>
      );
    }
    if (
      currentStep === ENROLMENT_STEP.QUESTIONS_CHALLENGE &&
      stepStatusToRender === ENROLMENT_STEP_STATUS.STARTED
    ) {
      if (questions) {
        return (
          <QuestionsChallengeForm
            questions={questions}
            onError={handleEnrolmentError}
          />
        );
      }
      return (
        <>
          <FormHeader />
          <FormContentContainer flex={1} disableGutters>
            <FormBody justifyContent="center">
              <FullScreenLoader title="Cargando preguntas" />
            </FormBody>
          </FormContentContainer>
        </>
      );
    }
    return (
      <>
        <FormHeader />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Cargando" />
          </FormBody>
        </FormContentContainer>
      </>
    );
  };

  return (
    <>
      {renderScreen()}
      {enrolmentErrorModals}
    </>
  );
};
